import React, { useEffect, useState } from 'react';

import { Button, Divider, InfoBox, TitleWithDescription } from '@infoods/lifebase-ui';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { useToggle } from 'react-use';

import classes from './EntityPlanWidget.module.scss';
import { SelfBillingNextPaymentInfo } from '../../../../../interfaces/selfBillingNextPaymentInfo';
import { stripeService } from '../../../../../services/stripeService';
import { getFormattedDate } from '../../../../../../shared/utils/dateUtils';
import {
  localDataFormat,
  dateFormat,
} from '../../../../../../shared/constants/apiDateFormat';
import { getPriceView } from '../../../../../../shared/utils/stringUtils';
import { CostBreakdownModal } from './CostBreakdownModal/CostBreakdownModal';

type EntityPlanWidgetProps = {
  entityId: number;
};

const EntityPlanWidget = ({ entityId }: EntityPlanWidgetProps) => {
  const [isShowCostBreakdownModal, toggleShowCostBreakdownModal] = useToggle(false);
  const [nextPaymentInfo, setNextPaymentInfo] =
    useState<SelfBillingNextPaymentInfo | null>(null);
  const [initInfoDate, setInitInfoDate] = useState<Moment | null>(null);

  const getNextPaymentInfo = async () => {
    const info = await stripeService.getSelfBillingNextPaymentInfo(entityId);

    setNextPaymentInfo(info);
    setInitInfoDate(moment());
  };

  useEffect(() => {
    getNextPaymentInfo();
  }, [entityId]);

  return (
    <>
      <InfoBox title="Entity Plan">
        {nextPaymentInfo && (
          <div className={classes.box}>
            <p className={classes.description}>
              <span>Your next monthly invoice will be issued&nbsp;</span>
              <strong className={classNames(classes.strong, 'private-info')}>
                {getFormattedDate(
                  nextPaymentInfo.nextBillingDate,
                  localDataFormat,
                  dateFormat,
                )}
              </strong>
              .
              <br />
              <span>We&apos;ll notify you beforehand via email.</span>
            </p>
            <div className={classes.planBox}>
              <div className={classNames(classes.row, classes.feeBox)}>
                <div>Active Client Fee</div>
                <div className="private-info">
                  {nextPaymentInfo.activeClients} Clients
                </div>
              </div>
              <Divider flexItem />
              <div className={classes.row}>
                <TitleWithDescription
                  title="Estimated Upcoming Total"
                  description={
                    <span>
                      It clearly shows all platform costs and <br /> the upcoming payment
                      total
                    </span>
                  }
                  titleFont="title-16-bold"
                />
                <div className={classes.priceBox}>
                  <span className={classNames(classes.price, 'private-info')}>
                    {getPriceView(nextPaymentInfo.upcomingTotal)}
                  </span>
                  <Button
                    variant="link"
                    className={classes.button}
                    onClick={toggleShowCostBreakdownModal}
                  >
                    Cost Breakdown
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </InfoBox>
      {nextPaymentInfo && initInfoDate && (
        <CostBreakdownModal
          open={isShowCostBreakdownModal}
          nextPaymentInfo={nextPaymentInfo}
          initInfoDate={initInfoDate}
          onClose={toggleShowCostBreakdownModal}
        />
      )}
    </>
  );
};

export { EntityPlanWidget };
